/* eslint-disable react/jsx-props-no-spreading */
import {
  Avatar,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { CiCircleCheck, CiCirclePlus } from 'react-icons/ci';
import { CompletionStatus, UserContainer } from '@/client/services/api/graphql/gql/graphql';
import {
  FilterOption,
  SearchGroup,
  SearchItem,
  convertAdminContentToSearchItem,
  convertUserToSearchItem,
  createTextQuery,
} from './WorkflowSearchTypes';
import {
  Search,
  TrackArticle,
  TrackAssessment,
  TrackFile,
  TrackScorm,
  TrackVideo,
} from '@/client/components/icons/ContinuIcons';
import { useEffect, useState } from 'react';

import type { AdminSearchContent } from '@/client/types/admin/AdminSearchContent';
import NoSearchResultsListItem from '../../create/content/lists/NoSearchResultsListItem';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { learnV4ApiClient } from '@/client/services/api/clients/learnV4ApiClient';
import { searchUsersQuery } from '../api/queries/searchUsersQuery';
import { useCombobox } from 'downshift';
import { useDebounceValue } from 'usehooks-ts';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWorkflowSearchStore } from '../state/workflowSearchStore';

interface WorkflowSearchProps {
  includeAssignments?: boolean;
  includeCompletionStatusFilter?: boolean;
}

export default function WorkflowSearch({
  includeAssignments = false,
  includeCompletionStatusFilter = false,
}: WorkflowSearchProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    selectedItems,
    setSelectedItems,
    selectedFilterOption,
    setSelectedFilterOption,
    filterOptions,
    setFilterOptions,
    selectedCompletionStatus,
    setSelectedCompletionStatus,
  } = useWorkflowSearchStore();
  const [inputFocused, setInputFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useDebounceValue<string | undefined>(undefined, 300);

  const completionStatusOptions = [
    { label: t('workflows.completionStatus.all'), value: undefined },
    {
      label: t('workflows.completionStatus.completedOnTime'),
      value: CompletionStatus.CompletedOnTime,
    },
    { label: t('workflows.completionStatus.inProgress'), value: CompletionStatus.InProgress },
    { label: t('workflows.completionStatus.completedLate'), value: CompletionStatus.CompletedLate },
    { label: t('workflows.completionStatus.pendingOnTime'), value: CompletionStatus.PendingOnTime },
    { label: t('workflows.completionStatus.notStarted'), value: CompletionStatus.NotStarted },
    { label: t('workflows.completionStatus.overdue'), value: CompletionStatus.Overdue },
    { label: t('workflows.completionStatus.unassigned'), value: CompletionStatus.Unassigned },
  ];

  const contentTypeIconMap: Record<string, React.ElementType> = {
    article: TrackArticle,
    assessment: TrackAssessment,
    file: TrackFile,
    scorm: TrackScorm,
    video: TrackVideo,
    track: TrackArticle,
    journey: TrackArticle,
    imported_content: TrackArticle,
    workshop: TrackArticle,
  };

  useEffect(() => {
    setSelectedItems([]);
  }, [selectedFilterOption]);

  useEffect(() => {
    setSelectedFilterOption({
      label: t('workflows.overview.search.users'),
      value: 'users',
    });

    let filters: FilterOption[] = [
      { label: t('workflows.overview.search.users'), value: 'users' },
      { label: t('workflows.overview.search.content'), value: 'content' },
    ];

    if (pathname.includes('overview')) {
      filters = [
        { label: t('workflows.overview.search.assignments'), value: 'assignments' },
        ...filters,
      ];

      setSelectedFilterOption({
        label: t('workflows.overview.search.assignments'),
        value: 'assignments',
      });
    }

    setFilterOptions(filters);
  }, [pathname]);

  const searchAdminContent = async (term: string | undefined): Promise<SearchGroup[]> => {
    const response: { data: { data: AdminSearchContent[]; next_exists: boolean } } =
      await learnV4ApiClient.get('admin-content/analytics', {
        params: {
          $or: `10,title,$regex,2,${term},i,name,$regex,2,${term},i`,
          page: '1',
          per_page: '20',
          types: 'articles,videos,files,scorm,assessments,tracks,journeys,imported_content',
        },
      });

    return [
      {
        label: t('workflows.overview.searchResults.content'),
        options: response.data.data.map(convertAdminContentToSearchItem),
      },
    ];
  };

  const { data: contentData } = useQuery({
    enabled: !!searchTerm && selectedFilterOption.value === 'content',
    queryKey: ['workflow-search-content', searchTerm],
    queryFn: () => searchAdminContent(searchTerm),
  });

  // Do we want to include suspended users in this search?
  const { data: usersData } = useQuery({
    enabled: !!searchTerm && selectedFilterOption.value === 'users',
    queryKey: ['workflow-search-users', searchTerm],
    queryFn: async () => {
      const usersResponse: { users: UserContainer } = await graphQLClient.request(
        searchUsersQuery,
        {
          name: { contains: searchTerm },
        },
      );

      return [
        {
          label: t('workflows.overview.searchResults.users'),
          options: usersResponse.users.edges.map((edge) => convertUserToSearchItem(edge.node)),
        },
      ];
    },
  });

  const searchData = selectedFilterOption.value === 'users' ? usersData : contentData;

  const flattenGroupOptions = (options: SearchGroup[]) =>
    options.reduce((prev: SearchItem[], curr: SearchGroup) => [...prev, ...curr.options], []);

  const { getMenuProps, getInputProps, highlightedIndex, getItemProps } = useCombobox<SearchItem>({
    onInputValueChange({ inputValue }) {
      setSearchTerm(inputValue);
    },
    items: searchData ? flattenGroupOptions(searchData) : [],
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      const existingMatch = selectedItems.find((item) => item.id === selectedItem.id);

      if (existingMatch) {
        setSelectedItems(selectedItems.filter((item) => item.id !== selectedItem.id));
      } else {
        setSelectedItems([...selectedItems, selectedItem]);
      }

      setSearchTerm('');
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (selectedFilterOption.value === 'assignments' && state.inputValue) {
            const textQuery = createTextQuery(state.inputValue);
            setSelectedItems([textQuery]);
            return {
              ...changes,
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
              inputValue: '',
            };
          }
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: state.inputValue,
          };
        default:
          return changes;
      }
    },
  });

  return (
    <HStack>
      <Stack spacing={2} position="relative">
        <Flex
          border="1px solid"
          borderColor={inputFocused ? 'warmNeutral.500' : 'warmNeutral.200'}
          borderRadius="lg"
          background="white"
          display="flex"
          minWidth="300px"
          maxWidth="800px"
          overflowX="hidden"
        >
          {!!selectedItems.length && (
            <HStack
              width="fit-content"
              maxWidth="400px"
              paddingX={2}
              overflowX="auto"
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              }}
              ref={(el) => {
                if (el) {
                  // eslint-disable-next-line no-param-reassign
                  el.scrollLeft = el.scrollWidth;
                }
              }}
            >
              {selectedItems.map((selectedItem, index: number) => (
                <Tag
                  key={`selected_item_${index + 1}`}
                  margin="0"
                  flexShrink={0}
                  background="warmNeutral.100"
                  border="1px solid"
                  borderColor="warmNeutral.300"
                  borderRadius="6px"
                  color="baseBlack"
                >
                  {selectedItem.__typename === 'User' && (
                    <Avatar
                      size="2xs"
                      marginRight={2}
                      name={selectedItem.fullName || ''}
                      src={selectedItem.image as string}
                    />
                  )}

                  {selectedItem.__typename === 'AdminSearchContent' && (
                    <Circle backgroundColor="warmNeutral.200" size="15px" marginRight={2}>
                      <Icon color="neutral.1200" as={contentTypeIconMap[selectedItem.type]} />
                    </Circle>
                  )}

                  <TagLabel
                    fontSize="14px"
                    fontWeight={500}
                    color="baseBlack"
                    display="inline-block"
                    whiteSpace="nowrap"
                  >
                    {selectedItem.__typename === 'User' && selectedItem.fullName}
                    {selectedItem.__typename === 'AdminSearchContent' && selectedItem.title}
                    {selectedItem.__typename === 'textQuery' && selectedItem.title}
                  </TagLabel>

                  <TagCloseButton
                    color="baseBlack"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedItems(selectedItems.filter((item) => item !== selectedItem));
                    }}
                  />
                </Tag>
              ))}
            </HStack>
          )}

          <InputGroup width="350px">
            {!selectedItems.length && (
              <InputLeftElement pointerEvents="none">
                <Search color="neutral.300" />
              </InputLeftElement>
            )}

            <Input
              {...getInputProps({
                onKeyDown: (event: React.KeyboardEvent) => {
                  const target = event.target as HTMLInputElement;
                  if (
                    (event.key === 'Backspace' || event.key === 'Delete') &&
                    target.value === '' &&
                    selectedItems.length > 0
                  ) {
                    const lastItem = selectedItems[selectedItems.length - 1];
                    setSelectedItems(selectedItems.filter((item) => item !== lastItem));
                  }
                },
              })}
              variant="create"
              border="none"
              width="200px"
              minWidth="200px"
              maxWidth="200px"
              placeholder={`${t('workflows.overview.search.placeholder')}`}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
            />

            <InputRightElement width="fit-content">
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  color="baseBlack"
                  fontSize="14px"
                  fontWeight={500}
                  rightIcon={<ChevronDownIcon boxSize={6} />}
                  _active={{
                    backgroundColor: 'transparent',
                  }}
                  _hover={{
                    backgroundColor: 'transparent',
                  }}
                >
                  {selectedFilterOption.label}
                </MenuButton>

                <Portal>
                  <MenuList
                    paddingX={4}
                    paddingY={2}
                    borderColor="warmNeutral.200"
                    borderRadius="6px"
                    marginTop={1}
                    boxShadow="0px 4px 12px 0px #00000026;
"
                  >
                    {filterOptions.map((filter, index) => (
                      <>
                        <MenuItem
                          key={filter.value}
                          paddingY={4}
                          borderRadius="6px"
                          _hover={{
                            backgroundColor: 'warmNeutral.0',
                          }}
                          _focus={{
                            backgroundColor: 'warmNeutral.0',
                          }}
                          onClick={() => setSelectedFilterOption(filter)}
                        >
                          <HStack>
                            {filter.value === selectedFilterOption.value && <CheckIcon />}

                            <Text fontSize="14px" fontWeight={500}>
                              {filter.label}
                            </Text>
                          </HStack>
                        </MenuItem>

                        {index !== filterOptions.length - 1 && (
                          <MenuDivider borderColor="warmNeutral.200" />
                        )}
                      </>
                    ))}
                  </MenuList>
                </Portal>
              </Menu>
            </InputRightElement>
          </InputGroup>
        </Flex>

        <List
          {...getMenuProps()}
          position="absolute"
          right={0}
          left={0}
          top={12}
          flexDirection="column"
          border={selectedFilterOption.value !== 'assignments' && searchData ? '1px solid' : 'none'}
          borderColor="warmNeutral.500"
          borderRadius="md"
          background="white"
          overflow="scroll"
          paddingX={6}
          zIndex={9999}
          maxH={400}
          width="inherit"
        >
          {searchData && searchData[0].options.length === 0 && <NoSearchResultsListItem />}

          {selectedFilterOption.value !== 'assignments' &&
            searchData &&
            searchData[0].options.length > 0 &&
            searchData.reduce(
              (
                results: { sections: JSX.Element[]; itemIndex: number },
                section: SearchGroup,
                sectionIndex: number,
              ) => {
                results.sections.push(
                  <Box
                    key={`results_section_${sectionIndex + 1}`}
                    paddingY={4}
                    borderBottom="1px solid"
                    borderColor="warmNeutral.100"
                  >
                    {section.options.length > 0 && (
                      <Text variant="createLabel" marginY={2}>
                        {section.label}
                      </Text>
                    )}

                    {section.options.length > 0 &&
                      section.options.map((option: SearchItem, optionIndex: number) => {
                        // eslint-disable-next-line no-plusplus, no-param-reassign
                        const resultIndex = results.itemIndex++;

                        const isSelected = selectedItems.find((item) => item.id === option.id);

                        return (
                          <ListItem
                            key={`option_index_${optionIndex + 1}`}
                            paddingX={4}
                            paddingY={2}
                            _hover={{
                              cursor: 'pointer',
                            }}
                            {...getItemProps({
                              item: option,
                              index: resultIndex,
                              'aria-selected': !!isSelected,
                            })}
                            backgroundColor={
                              highlightedIndex === resultIndex ? 'warmNeutral.0' : 'white'
                            }
                            border={highlightedIndex === resultIndex ? '1px solid' : 'none'}
                            borderColor="warmNeutral.400"
                            borderRadius="6px"
                          >
                            <Flex alignItems="center" width="full" justifyContent="space-between">
                              {option.__typename === 'AdminSearchContent' && (
                                <Circle
                                  backgroundColor={isSelected ? 'gray.50' : 'gray.100'}
                                  size="30px"
                                >
                                  <Icon
                                    color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                    as={contentTypeIconMap[option.type]}
                                  />
                                </Circle>
                              )}

                              {option.__typename === 'User' && (
                                <Avatar size="sm" name={option.fullName} src={option.image} />
                              )}

                              {option.__typename === 'textQuery' && (
                                <Circle
                                  backgroundColor="warmNeutral.200"
                                  size="15px"
                                  marginRight={2}
                                >
                                  <Icon color="neutral.1200" as={Search} />
                                </Circle>
                              )}

                              <Box flexGrow={1} textAlign="left" paddingLeft={4}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={500}
                                  color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                >
                                  {option.__typename === 'User' && option.fullName}
                                  {option.__typename === 'AdminSearchContent' && option.title}
                                  {option.__typename === 'textQuery' && option.title}
                                </Text>
                              </Box>

                              <Icon
                                boxSize={8}
                                as={isSelected ? CiCircleCheck : CiCirclePlus}
                                color={isSelected ? 'warmNeutral.300' : 'neutral.800'}
                              />
                            </Flex>
                          </ListItem>
                        );
                      })}
                  </Box>,
                );

                return results;
              },
              { sections: [], itemIndex: 0 },
            ).sections}
        </List>
      </Stack>

      {includeCompletionStatusFilter && (
        <Menu>
          <MenuButton
            as={Button}
            variant="ghost"
            color="baseBlack"
            fontSize="14px"
            fontWeight={500}
            rightIcon={<ChevronDownIcon boxSize={6} />}
            backgroundColor="white"
            border="1px solid"
            borderColor="warmNeutral.200"
            borderRadius="6px"
            _active={{
              backgroundColor: 'transparent',
            }}
            _hover={{
              backgroundColor: 'transparent',
            }}
          >
            {selectedCompletionStatus?.value
              ? selectedCompletionStatus.label
              : t('workflows.completionStatus')}
          </MenuButton>

          <Portal>
            <MenuList
              paddingX={4}
              paddingY={2}
              borderColor="warmNeutral.200"
              borderRadius="6px"
              marginTop={1}
              boxShadow="0px 4px 12px 0px #00000026;"
              maxHeight={250}
              overflowY="auto"
            >
              {completionStatusOptions.map((option, index) => (
                <>
                  <MenuItem
                    key={option.value}
                    paddingY={4}
                    borderRadius="6px"
                    _hover={{
                      backgroundColor: 'warmNeutral.0',
                    }}
                    _focus={{
                      backgroundColor: 'warmNeutral.0',
                    }}
                    onClick={() => setSelectedCompletionStatus(option)}
                  >
                    <HStack>
                      {option.value === selectedCompletionStatus?.value && <CheckIcon />}

                      <Text fontSize="14px" fontWeight={500}>
                        {option.label}
                      </Text>
                    </HStack>
                  </MenuItem>

                  {index !== completionStatusOptions.length - 1 && (
                    <MenuDivider borderColor="warmNeutral.200" />
                  )}
                </>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </HStack>
  );
}
